import { useEffect, useState } from "react";
import { api, formatApiErrorDetail } from "@/lib/api";
import { Plus, Save, Trash2 } from "lucide-react";
import { toast } from "sonner";

const SUP_DERECHOS = ["18", "17", "16", "15", "14", "13", "12", "11"];
const SUP_IZQUIERDOS = ["21", "22", "23", "24", "25", "26", "27", "28"];
const INF_IZQUIERDOS = ["38", "37", "36", "35", "34", "33", "32", "31"];
const INF_DERECHOS = ["41", "42", "43", "44", "45", "46", "47", "48"];

function todayStr() { return new Date().toISOString().slice(0, 10); }

const EMPTY_TOOTH = () => ({
  surco: [0, 0, 0],
  margen: [0, 0, 0],
  nic: [0, 0, 0],
  furca: false,
  exudado: false,
  sangramiento: false,
  movilidad: 0,
});

function ToothColumn({ code, mid, onChange }) {
  const m = mid || EMPTY_TOOTH();
  const handleArr = (key, idx, v) => {
    const arr = [...(m[key] || [0, 0, 0])];
    arr[idx] = Math.max(0, Math.min(15, Number(v) || 0));
    onChange({ ...m, [key]: arr });
  };
  return (
    <div className="border-r border-border" data-testid={`tooth-col-${code}`}>
      <div className="text-center py-1 font-mono text-[10px] font-bold border-b border-border bg-muted/50">{code}</div>
      {/* surco */}
      <div className="grid grid-cols-3 border-b border-border">
        {[0, 1, 2].map((i) => (
          <input key={i} type="number" min="0" max="15"
            value={m.surco[i] ?? 0}
            onChange={(e) => handleArr("surco", i, e.target.value)}
            className="w-full text-center font-mono text-[10px] py-1 border-r last:border-r-0 border-border bg-transparent focus:bg-primary/10 outline-none"
          />
        ))}
      </div>
      {/* margen */}
      <div className="grid grid-cols-3 border-b border-border">
        {[0, 1, 2].map((i) => (
          <input key={i} type="number" min="0" max="15"
            value={m.margen[i] ?? 0}
            onChange={(e) => handleArr("margen", i, e.target.value)}
            className="w-full text-center font-mono text-[10px] py-1 border-r last:border-r-0 border-border bg-transparent focus:bg-primary/10 outline-none text-destructive"
          />
        ))}
      </div>
      {/* nic */}
      <div className="grid grid-cols-3 border-b border-border bg-muted/30">
        {[0, 1, 2].map((i) => (
          <input key={i} type="number" min="0" max="15"
            value={m.nic[i] ?? 0}
            onChange={(e) => handleArr("nic", i, e.target.value)}
            className="w-full text-center font-mono text-[10px] py-1 border-r last:border-r-0 border-border bg-transparent focus:bg-primary/10 outline-none font-bold"
          />
        ))}
      </div>
      {/* furca */}
      <div className="text-center py-1 border-b border-border h-7">
        <input type="checkbox" checked={!!m.furca}
          onChange={(e) => onChange({ ...m, furca: e.target.checked })}
          className="w-3.5 h-3.5 accent-primary"
        />
      </div>
      {/* exudado */}
      <div className="text-center py-1 border-b border-border h-7">
        <input type="checkbox" checked={!!m.exudado}
          onChange={(e) => onChange({ ...m, exudado: e.target.checked })}
          className="w-3.5 h-3.5 accent-accent" style={{ accentColor: "#D97757" }}
        />
      </div>
      {/* sangramiento */}
      <div className="text-center py-1 border-b border-border h-7">
        <input type="checkbox" checked={!!m.sangramiento}
          onChange={(e) => onChange({ ...m, sangramiento: e.target.checked })}
          className="w-3.5 h-3.5" style={{ accentColor: "#B42318" }}
        />
      </div>
      {/* movilidad */}
      <div className="text-center py-1 h-7">
        <input type="number" min="0" max="3"
          value={m.movilidad ?? 0}
          onChange={(e) => onChange({ ...m, movilidad: Math.max(0, Math.min(3, Number(e.target.value) || 0)) })}
          className="w-full text-center font-mono text-[10px] bg-transparent focus:bg-primary/10 outline-none"
        />
      </div>
    </div>
  );
}

function ArchTable({ teethCodes, mediciones, setMediciones, label }) {
  return (
    <div className="card-flat overflow-hidden">
      <div className="px-4 py-2 border-b border-border bg-muted/40">
        <div className="font-display font-bold text-sm tracking-tight">{label}</div>
      </div>
      <div className="grid" style={{ gridTemplateColumns: `120px repeat(${teethCodes.length}, minmax(54px, 1fr))` }}>
        <div className="border-r border-border">
          <div className="py-1 px-2 font-display font-bold text-[10px] uppercase tracking-widest border-b border-border bg-muted/50">#Pieza</div>
          <div className="py-1 px-2 text-[10px] font-bold border-b border-border" style={{ color: "#7A4FBA" }}>Profundidad surco</div>
          <div className="py-1 px-2 text-[10px] font-bold border-b border-border text-destructive">Margen</div>
          <div className="py-1 px-2 text-[10px] font-bold border-b border-border bg-muted/30">NIC</div>
          <div className="py-1 px-2 text-[10px] border-b border-border h-7">Furca</div>
          <div className="py-1 px-2 text-[10px] border-b border-border h-7">Exudado</div>
          <div className="py-1 px-2 text-[10px] border-b border-border h-7">Sangramiento</div>
          <div className="py-1 px-2 text-[10px] h-7">Movilidad</div>
        </div>
        {teethCodes.map((code) => (
          <ToothColumn key={code} code={code}
            mid={mediciones[code]}
            onChange={(v) => setMediciones({ ...mediciones, [code]: v })}
          />
        ))}
      </div>
    </div>
  );
}

export default function Periodontograma({ patientId }) {
  const [list, setList] = useState([]);
  const [activeId, setActiveId] = useState("nuevo");
  const [fecha, setFecha] = useState(todayStr());
  const [arcada, setArcada] = useState("superior");
  const [mediciones, setMediciones] = useState({});
  const [notas, setNotas] = useState("");
  const [saving, setSaving] = useState(false);

  const load = async () => {
    try {
      const { data } = await api.get(`/patients/${patientId}/periodontogramas`);
      setList(data);
    } catch (e) { /* noop */ }
  };
  useEffect(() => { load(); }, [patientId]); // eslint-disable-line

  useEffect(() => {
    if (activeId === "nuevo") {
      setFecha(todayStr()); setArcada("superior"); setMediciones({}); setNotas("");
    } else {
      const it = list.find((x) => x.id === activeId);
      if (it) { setFecha(it.fecha); setArcada(it.arcada); setMediciones(it.mediciones || {}); setNotas(it.notas || ""); }
    }
  }, [activeId, list]);

  const save = async () => {
    setSaving(true);
    try {
      const payload = { fecha, arcada, mediciones, notas };
      if (activeId === "nuevo") {
        const { data } = await api.post(`/patients/${patientId}/periodontogramas`, payload);
        toast.success("Periodontograma guardado");
        await load();
        setActiveId(data.id);
      } else {
        await api.put(`/periodontogramas/${activeId}`, payload);
        toast.success("Periodontograma actualizado");
        await load();
      }
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
    finally { setSaving(false); }
  };

  const remove = async (id) => {
    if (!window.confirm("¿Eliminar periodontograma?")) return;
    try { await api.delete(`/periodontogramas/${id}`); await load(); setActiveId("nuevo"); }
    catch { toast.error("No se pudo eliminar"); }
  };

  const upperTeeth = [...SUP_DERECHOS, ...SUP_IZQUIERDOS];
  const lowerTeeth = [...INF_DERECHOS, ...INF_IZQUIERDOS];

  return (
    <div className="space-y-4" data-testid="periodontograma">
      <div className="flex items-end justify-between flex-wrap gap-3">
        <div>
          <h2 className="font-display font-bold text-2xl tracking-tight">Periodontograma</h2>
          <p className="text-sm text-muted-foreground">Mediciones periodontales: profundidad, margen, NIC, furca, sangrado, movilidad.</p>
        </div>
        <div className="flex items-end gap-2 flex-wrap">
          <select value={activeId} onChange={(e) => setActiveId(e.target.value)} className="input-flat" data-testid="period-version">
            <option value="nuevo">+ Nuevo periodontograma</option>
            {list.map((it) => (
              <option key={it.id} value={it.id}>{it.fecha} · arcada {it.arcada}</option>
            ))}
          </select>
          {activeId !== "nuevo" && (
            <button onClick={() => remove(activeId)} className="px-3 h-10 rounded-sm border border-border hover:bg-destructive/10 text-destructive" title="Eliminar">
              <Trash2 className="w-4 h-4" />
            </button>
          )}
          <button onClick={save} disabled={saving} className="btn-primary" data-testid="period-save">
            <Save className="w-4 h-4" /> {saving ? "Guardando…" : "Guardar"}
          </button>
        </div>
      </div>

      <div className="card-flat p-4 grid grid-cols-1 sm:grid-cols-3 gap-3">
        <div>
          <label className="label-eyebrow block mb-1">Fecha</label>
          <input type="date" className="input-flat font-mono" value={fecha} onChange={(e) => setFecha(e.target.value)} />
        </div>
        <div>
          <label className="label-eyebrow block mb-1">Arcada</label>
          <select className="input-flat" value={arcada} onChange={(e) => setArcada(e.target.value)}>
            <option value="superior">Maxilar superior</option>
            <option value="inferior">Maxilar inferior</option>
          </select>
        </div>
        <div className="sm:col-span-1">
          <label className="label-eyebrow block mb-1">Notas</label>
          <input className="input-flat" value={notas} onChange={(e) => setNotas(e.target.value)} placeholder="Observaciones del clínico" />
        </div>
      </div>

      <div className="space-y-3 overflow-x-auto">
        {arcada === "superior" ? (
          <ArchTable teethCodes={upperTeeth} mediciones={mediciones} setMediciones={setMediciones} label="Maxilar superior — vestibular" />
        ) : (
          <ArchTable teethCodes={lowerTeeth} mediciones={mediciones} setMediciones={setMediciones} label="Maxilar inferior — vestibular" />
        )}
      </div>

      <div className="card-flat p-4 grid grid-cols-2 sm:grid-cols-4 gap-3 text-xs">
        <div className="flex items-center gap-2"><span className="w-3 h-3 rounded-full" style={{ background: "#7A4FBA" }} /><span><b>Profundidad surco</b> · 3 puntos por diente</span></div>
        <div className="flex items-center gap-2"><span className="w-3 h-3 rounded-full bg-destructive" /><span><b>Margen gingival</b></span></div>
        <div className="flex items-center gap-2"><span className="w-3 h-3 rounded-full bg-muted-foreground" /><span><b>NIC</b> = surco + margen</span></div>
        <div className="flex items-center gap-2"><span className="w-3 h-3 rounded-full" style={{ background: "#D97757" }} /><span><b>Exudado</b> y <b>sangramiento</b></span></div>
      </div>
    </div>
  );
}
